import { axiosInstance } from "contexts/axiosApiInstance";
import type { McpPeriod } from "@/lib/api/organizations";
import type {
  AnswerRequest,
  AskingRequest,
  AppDetail,
  AppListItem,
  AppPage,
  AppSetup,
  ConfirmAllResponse,
  CreateAppRequest,
  CreateDesignRequest,
  DesignApp,
  DraftRound,
  DraftTable,
  Lending,
  ModelTable,
  ProviderCatalog,
  ProviderKeyUsage,
  MySpend,
  Question,
  QuestionsResponse,
  RunDetail,
} from "./types";

/** The server's `{error, message}` refusal, surfaced with its code. */
export class AppApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** The refusal's own fields beyond `error` and `message`. */
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "AppApiError";
  }
}

function toAppError(error: unknown): AppApiError {
  const e = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = e.response?.status ?? 0;
  const data = e.response?.data;
  const fallback = e.message ?? "request failed";
  if (data && typeof data === "object") {
    const body = data as { error?: string; message?: string };
    return new AppApiError(
      status,
      body.error ?? "request_failed",
      body.message ?? fallback,
      data as Record<string, unknown>,
    );
  }
  if (typeof data === "string" && data.trim()) {
    // A proxy's own error page, never a door's refusal.
    if (data.trimStart().startsWith("<"))
      return status === 504
        ? new AppApiError(
            status,
            "gateway_timeout",
            "The connection timed out before the server answered.",
          )
        : new AppApiError(
            status,
            "request_failed",
            `The server could not be reached (HTTP ${status}).`,
          );
    return new AppApiError(status, "request_failed", data);
  }
  return new AppApiError(status, "request_failed", fallback);
}

export async function call<T>(request: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await request()).data;
  } catch (error) {
    throw toAppError(error);
  }
}

const appPath = (appId: string, rest = "") =>
  `/action/apps/${encodeURIComponent(appId)}${rest}`;

export function listApps(orgId: string): Promise<AppListItem[]> {
  return call(() =>
    axiosInstance.get<AppListItem[]>("/action/apps", {
      params: { org_id: orgId },
    }),
  );
}

export function createApp(request: CreateAppRequest): Promise<AppDetail> {
  return call(() =>
    axiosInstance.post<AppDetail>("/action/apps/research", request),
  );
}

/** Makes the app and queues its first round, which `agent_service` runs. */
export function createDesign(
  request: CreateDesignRequest,
): Promise<{ app_id: string; run_id: string }> {
  return call(() => axiosInstance.post("/action/apps/design", request));
}

export function fetchApp(appId: string): Promise<AppPage> {
  return call(() => axiosInstance.get<AppPage>(appPath(appId)));
}

export function updateApp(appId: string, setup: AppSetup): Promise<AppDetail> {
  return call(() => axiosInstance.put<AppDetail>(appPath(appId), setup));
}

export type DesignSetupChange = Partial<
  Pick<DesignApp, "name" | "cost_limit_cents" | "target_model_id">
>;

/** The design's setup as it stands, with `change` laid over it. */
export function saveDesignSetup(
  app: DesignApp,
  { name = app.name, ...change }: DesignSetupChange,
): Promise<unknown> {
  return call(() =>
    axiosInstance.patch(appPath(app.app_id), {
      name,
      config: {
        brief: app.brief,
        target_model_id: app.target_model_id,
        provider: app.provider,
        model: app.model,
        cost_limit_cents: app.cost_limit_cents,
        ...change,
      },
      base_revision: app.setup_revision,
    }),
  );
}

export function appAction(
  appId: string,
  action: "pause" | "resume",
): Promise<AppDetail> {
  return call(() =>
    axiosInstance.post<AppDetail>(appPath(appId, `/${action}`)),
  );
}

export function deleteApp(appId: string): Promise<void> {
  return call(() => axiosInstance.delete<void>(appPath(appId)));
}

export function fetchQuestions(appId: string): Promise<QuestionsResponse> {
  return call(() =>
    axiosInstance.get<QuestionsResponse>(appPath(appId, "/questions")),
  );
}

export function answerQuestion(
  appId: string,
  questionId: string,
  answer: AnswerRequest,
): Promise<Question> {
  return call(() =>
    axiosInstance.post<Question>(
      appPath(appId, `/questions/${encodeURIComponent(questionId)}/answer`),
      answer,
    ),
  );
}

/** Deletes a settled clarification: no run reads it again, and what its answer
 *  applied stays. */
export function deleteQuestion(
  appId: string,
  questionId: string,
): Promise<void> {
  return call(() =>
    axiosInstance.delete<void>(
      appPath(appId, `/questions/${encodeURIComponent(questionId)}`),
    ),
  );
}

/** The one asking door: the round's answers, then the next round or the
 *  end; on an app that is not asking, it starts asking. */
export function askingStep(
  appId: string,
  request: AskingRequest,
): Promise<AppDetail> {
  return call(() =>
    axiosInstance.post<AppDetail>(appPath(appId, "/asking"), request),
  );
}

/** Lends a table to the app, or changes how it is lent (docs/Apps.md § 4). */
export function lendTable(appId: string, lending: Lending): Promise<unknown> {
  return call(() => axiosInstance.post(appPath(appId, "/grants"), lending));
}

export function takeBackTable(
  appId: string,
  tableId: string,
): Promise<unknown> {
  return call(() =>
    axiosInstance.delete(
      appPath(appId, `/grants/${encodeURIComponent(tableId)}`),
    ),
  );
}

export function startApp(appId: string): Promise<AppDetail> {
  return call(() => axiosInstance.post<AppDetail>(appPath(appId, "/start")));
}

export interface LinkEnd {
  table_id: string;
  table_name?: string;
  column_names?: string[];
}

export interface TableLink {
  id: string;
  first_item: LinkEnd;
  second_item: LinkEnd;
}

/** The links of one table the caller can see both ends of. */
export function fetchTableLinks(tableId: string): Promise<TableLink[]> {
  return call(() =>
    axiosInstance.get<TableLink[]>("/action/links", {
      params: { for_table: tableId },
    }),
  );
}

export function confirmAllPicks(appId: string): Promise<ConfirmAllResponse> {
  return call(() =>
    axiosInstance.post<ConfirmAllResponse>(
      appPath(appId, "/questions/confirm-all"),
    ),
  );
}

/** One step of any app: the run with what its calls cost, and its lines. */
export function fetchRun(appId: string, runId: string): Promise<RunDetail> {
  return call(() =>
    axiosInstance.get<RunDetail>(
      appPath(appId, `/runs/${encodeURIComponent(runId)}`),
    ),
  );
}

/** Asks the app's step to stop; the model call in flight is dropped. */
export async function stopApp(appId: string): Promise<void> {
  await call(() => axiosInstance.post(appPath(appId, "/stop")));
}

export function fetchProviders(orgId: string): Promise<ProviderCatalog> {
  return call(() =>
    axiosInstance.get<ProviderCatalog>("/action/apps/providers", {
      params: { org_id: orgId },
    }),
  );
}

export function fetchProviderKeyUsage(
  orgId: string,
  period: McpPeriod,
): Promise<ProviderKeyUsage> {
  return call(() =>
    axiosInstance.get<ProviderKeyUsage>("/action/apps/providers/usage", {
      params: { org_id: orgId, period },
    }),
  );
}

export function putProviderKey(
  provider: string,
  orgId: string,
  apiKey: string,
): Promise<ProviderCatalog> {
  return call(() =>
    axiosInstance.put<ProviderCatalog>(
      `/action/apps/providers/${encodeURIComponent(provider)}/key`,
      { org_id: orgId, api_key: apiKey },
    ),
  );
}

export function putProviderLimit(
  provider: string,
  orgId: string,
  limitCents: number | null,
): Promise<ProviderCatalog> {
  return call(() =>
    axiosInstance.put<ProviderCatalog>(
      `/action/apps/providers/${encodeURIComponent(provider)}/limit`,
      { org_id: orgId, limit_cents: limitCents },
    ),
  );
}

/** `centsPer100` is the price the confirmed terms named. */
export function putPaidSearches(
  orgId: string,
  on: boolean,
  centsPer100: number | null,
): Promise<ProviderCatalog> {
  return call(() =>
    axiosInstance.put<ProviderCatalog>("/action/apps/providers/paid-searches", {
      org_id: orgId,
      on,
      cents_per_100: centsPer100,
    }),
  );
}

export function putSearchLimit(
  orgId: string,
  limit: number | null,
): Promise<ProviderCatalog> {
  return call(() =>
    axiosInstance.put<ProviderCatalog>("/action/apps/providers/search-limit", {
      org_id: orgId,
      limit,
    }),
  );
}

/** `clientId` `null` sets the default every member without their own takes. */
export function putMemberLimit(
  orgId: string,
  clientId: string | null,
  limitCents: number | null,
): Promise<ProviderKeyUsage> {
  return call(() =>
    axiosInstance.put<ProviderKeyUsage>(
      "/action/apps/providers/member-limits",
      { org_id: orgId, client_id: clientId, limit_cents: limitCents },
    ),
  );
}

export function fetchMySpend(orgId: string): Promise<MySpend> {
  return call(() =>
    axiosInstance.get<MySpend>("/action/apps/providers/my-spend", {
      params: { org_id: orgId },
    }),
  );
}

export function deleteProviderKey(
  provider: string,
  orgId: string,
): Promise<ProviderCatalog> {
  return call(() =>
    axiosInstance.delete<ProviderCatalog>(
      `/action/apps/providers/${encodeURIComponent(provider)}/key`,
      { params: { org_id: orgId } },
    ),
  );
}

/** Queues the next round. */
export function sendDesignFeedback(
  appId: string,
  feedback: DraftRound,
): Promise<{ run_id: string }> {
  return call(() => axiosInstance.post(appPath(appId, "/feedback"), feedback));
}

/** A person's correction to a column's name in the latest version; no round. */
export function renameDraftColumn(
  appId: string,
  rename: { table: string; column: string; name: string },
): Promise<DesignApp> {
  return call(() =>
    axiosInstance.post<DesignApp>(appPath(appId, "/rename"), rename),
  );
}

export function setDraftUnit(
  appId: string,
  pick: {
    table: string;
    column: string;
    unit_uri: string | null;
    scale_exponent: number;
  },
): Promise<DesignApp> {
  return call(() =>
    axiosInstance.post<DesignApp>(appPath(appId, "/unit"), pick),
  );
}

/** `interfere`: the person saw the research runs on these tables and goes ahead. */
export function applyDesign(
  appId: string,
  interfere = false,
): Promise<DesignApp> {
  return call(() =>
    axiosInstance.post<DesignApp>(appPath(appId, "/apply"), { interfere }),
  );
}

/** Records the model as the latest version when someone changed the design's
 *  tables there since it last knew them. */
export function syncDesign(appId: string): Promise<DesignApp> {
  return call(() => axiosInstance.post<DesignApp>(appPath(appId, "/sync")));
}

export function fetchModelTables(
  orgId: string,
  modelId: string,
): Promise<ModelTable[]> {
  return call(() =>
    axiosInstance.get<ModelTable[]>(
      `/action/apps/models/${encodeURIComponent(modelId)}/tables`,
      { params: { org_id: orgId } },
    ),
  );
}

export function fetchSchemaSourceTables(
  orgId: string,
  modelId: string,
): Promise<DraftTable[]> {
  return call(() =>
    axiosInstance.get<DraftTable[]>(
      `/action/apps/schema-sources/${encodeURIComponent(modelId)}`,
      { params: { org_id: orgId } },
    ),
  );
}
